import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { graphql } from 'gatsby';
import useStaticFooter from 'hooks/useStaticFooter';
import useStaticHeader from 'hooks/useStaticHeader';
import useStaticSite from 'hooks/useStaticSite';
import { convert } from 'html-to-text';
import SEO from 'layouts/Seo';

import Footer from 'components/Footer';
import Header from 'components/Header';
import Hero from 'components/Hero';
import PostListing from 'components/Resources/PostListing';

import { contentfulProps } from 'utils/emptyTypes';

import 'styles/templates/PostListing.css';

import type { FooterProps } from 'components/Footer';
import type { HeaderProps } from 'components/Header';
import type { PageProps } from 'gatsby';
import type {
  ContentfulAsset,
  ContentfulComponentHero,
  ContentfulEntityPerson,
  ContentfulLayoutSection,
  ContentfulTaxonomyTag,
} from 'graphqlTypes';
import type { FC } from 'react';
import type { PostUnion } from 'types';

interface PostDataProps {
  pageData: ContentfulEntityPerson;
  conversionPanelSection: ContentfulLayoutSection;
}

type pageContextProps = {
  hasPreviousPage: boolean;
  pageNumber: number;
  authoredPosts: PostUnion[];
  resourceType: string;
  authorSlug: string;
  totalPages: number;
  totalPosts: number;
  tags: ContentfulTaxonomyTag[];
};

const AuthorListing: FC<PageProps<PostDataProps>> = ({
  data: {
    pageData: { bio, fullName, headshot },
  },
  pageContext,
  location,
}) => {
  const { header } = useStaticHeader();
  const { footer } = useStaticFooter();

  const { allPosts, authorSlug, hasPreviousPage, pageNumber, authoredPosts, tags, totalPages, totalPosts } =
    pageContext as pageContextProps;

  return (
    <div>
      {header && <Header {...(header as HeaderProps)} />}
      <main>
        <Hero
          className="mb-18 md:mb-24"
          eyebrow="Author"
          heading={fullName}
          content={bio as ContentfulComponentHero['content']}
          isFullHeightImage={false}
          alignment="Left Aligned"
          background="textureGreen"
          hasBreadcrumbs
          padding="small"
          breadcrumbsOverride={[
            {
              label: 'Resources',
              link: '/resource-center',
              ...contentfulProps,
            },
            {
              label: fullName,
              link: `/author/${authorSlug}`,
              ...contentfulProps,
            },
          ]}
          authorImage={headshot as ContentfulAsset}
          {...contentfulProps}
        />
        <PostListing
          allPosts={allPosts}
          posts={authoredPosts}
          currentPageNumber={pageNumber}
          resourceType="author"
          authorSlug={authorSlug}
          tags={tags}
          totalPages={totalPages}
          totalPosts={totalPosts}
          hasPreviousPage={hasPreviousPage}
          filters={location?.state?.filterValues}
          sort={location?.state?.sort}
          search={location?.state?.search}
        />
      </main>
      {footer && <Footer {...(footer as FooterProps)} background="blue900" padding="small" contained />}
    </div>
  );
};

export const Head: FC<PageProps<PostDataProps>> = ({
  data: {
    pageData: { fullName, headshot, bio },
  },
  pageContext,
}) => {
  const { pageNumber, authorSlug } = pageContext as pageContextProps;
  const { site } = useStaticSite();
  const slug = `author/${authorSlug}`;
  const canonicalUrl = `${site.siteMetadata.siteUrl}/${slug}${pageNumber > 1 ? `/page/${pageNumber}` : ''}`;

  let description = '';
  try {
    const rawBio = bio?.raw as string;
    if (rawBio) {
      const htmlDescription = documentToHtmlString(JSON.parse(rawBio));
      description = convert(htmlDescription, {
        wordwrap: false,
      });
    }
  } catch (error) {
    console.error('Error parsing JSON:', error);
  }

  return (
    <SEO
      pageTitle={`Aspire Author | ${fullName} | Page ${pageNumber}`}
      pageDescription={description}
      openGraphImage={headshot}
      indexable={pageNumber < 5}
      slug={slug}
      canonicalUrl={canonicalUrl}
    />
  );
};

export default AuthorListing;

export const postQuery = graphql`
  query templateAuthorListingQuery($authorId: String!) {
    pageData: contentfulEntityPerson(id: { eq: $authorId }) {
      ...contentfulEntityPerson
    }
    conversionPanelSection: contentfulLayoutSection(contentful_id: { eq: "4KXA4lFTbrhPADmbxqr6Si" }) {
      ...contentfulLayoutSection
    }
  }
`;
